import {useQuery} from "@tanstack/react-query"
import {studentStaffContactsServiceV3} from "services"
import {BaseDepartmentId} from "types/departments"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {sortBy} from "lodash"
import {getFullName} from "helpers"
import React from "react"
import {useStudentStaffContactRoles} from "hooks/useStudentStaffContactRoles"

export const useAdvisors = (campusIds?: number[]) => {
    return useQuery(
        ["dept-advisors", campusIds],
        async () => {
            const [
                {data: admissionAdvisors},
                {data: finAidAdvisors},
                {data: academicAdvisors},
                {data: studentServicesAdvisors},
                {data: studentAccountAdvisors},
                {data: careerServicesAdvisors}
            ] = await Promise.all([
                studentStaffContactsServiceV3.getAvailableAdvisorsForDepartment({
                    filter: {
                        campusIds,
                        departmentId: BaseDepartmentId.Admissions
                    },
                    range: {
                        pageSize: UNLIMITED_PAGE_SIZE,
                        page: 1
                    }
                }),
                studentStaffContactsServiceV3.getAvailableAdvisorsForDepartment({
                    filter: {
                        campusIds,
                        departmentId: BaseDepartmentId.FinancialAid
                    },
                    range: {
                        pageSize: UNLIMITED_PAGE_SIZE,
                        page: 1
                    }
                }),
                studentStaffContactsServiceV3.getAvailableAdvisorsForDepartment({
                    filter: {
                        campusIds,
                        departmentId: BaseDepartmentId.Academics
                    },
                    range: {
                        pageSize: UNLIMITED_PAGE_SIZE,
                        page: 1
                    }
                }),
                studentStaffContactsServiceV3.getAvailableAdvisorsForDepartment({
                    filter: {
                        campusIds,
                        departmentId: BaseDepartmentId.StudentServices
                    },
                    range: {
                        pageSize: UNLIMITED_PAGE_SIZE,
                        page: 1
                    }
                }),
                studentStaffContactsServiceV3.getAvailableAdvisorsForDepartment({
                    filter: {
                        campusIds,
                        departmentId: BaseDepartmentId.StudentAccount
                    },
                    range: {
                        pageSize: UNLIMITED_PAGE_SIZE,
                        page: 1
                    }
                }),
                studentStaffContactsServiceV3.getAvailableAdvisorsForDepartment({
                    filter: {
                        campusIds,
                        departmentId: BaseDepartmentId.CareerServices
                    },
                    range: {
                        pageSize: UNLIMITED_PAGE_SIZE,
                        page: 1
                    }
                })
            ])
            return {
                admissionAdvisors: sortBy(
                    admissionAdvisors.map((advisor) => ({
                        ...advisor,
                        fullName: getFullName(advisor),
                        name: getFullName(advisor)
                    })),
                    "firstName"
                ),
                finAidAdvisors: sortBy(
                    finAidAdvisors.map((advisor) => ({
                        ...advisor,
                        fullName: getFullName(advisor),
                        name: getFullName(advisor)
                    })),
                    "firstName"
                ),
                academicAdvisors: sortBy(
                    academicAdvisors.map((advisor) => ({
                        ...advisor,
                        fullName: getFullName(advisor),
                        name: getFullName(advisor)
                    })),
                    "firstName"
                ),
                studentServicesAdvisors: sortBy(
                    studentServicesAdvisors.map((advisor) => ({
                        ...advisor,
                        fullName: getFullName(advisor),
                        name: getFullName(advisor)
                    })),
                    "firstName"
                ),
                studentAccountAdvisors: sortBy(
                    studentAccountAdvisors.map((advisor) => ({
                        ...advisor,
                        fullName: getFullName(advisor),
                        name: getFullName(advisor)
                    })),
                    "firstName"
                ),
                careerServicesAdvisors: sortBy(
                    careerServicesAdvisors.map((advisor) => ({
                        ...advisor,
                        fullName: getFullName(advisor),
                        name: getFullName(advisor)
                    })),
                    "firstName"
                )
            }
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes - avoid refetching advisor list too often
            placeholderData: {
                admissionAdvisors: [],
                finAidAdvisors: [],
                academicAdvisors: [],
                studentServicesAdvisors: [],
                studentAccountAdvisors: [],
                careerServicesAdvisors: []
            }
        }
    )
}

export const useStaffRoleAdvisors = (roleId: number, campusIds?: number[]) => {
    const {staffContactRoles: staffRoles} = useStudentStaffContactRoles()
    const {data: advisorsData} = useAdvisors(campusIds)
    return React.useMemo(() => {
        if (roleId === staffRoles.find((role) => role.name.includes("Admission Advisor"))?.id) {
            return advisorsData.admissionAdvisors
        }
        if (
            roleId ===
            staffRoles.find(
                (role) => role.name.includes("Financial Aid Advisor") || role.name.includes("Financial Service Advisor")
            )?.id
        ) {
            return advisorsData.finAidAdvisors
        }
        if (roleId === staffRoles.find((role) => role.name.includes("Academic Advisor"))?.id) {
            return advisorsData.academicAdvisors
        }
        if (roleId === staffRoles.find((role) => role.name.includes("Student Services Advisor"))?.id) {
            return advisorsData.studentServicesAdvisors
        }
        if (roleId === staffRoles.find((role) => role.name.includes("Student Accounts Advisor"))?.id) {
            return advisorsData.studentAccountAdvisors
        }
        if (roleId === staffRoles.find((role) => role.name.includes("Career Services Advisor"))?.id) {
            return advisorsData.careerServicesAdvisors
        }
        return []
    }, [staffRoles, roleId, advisorsData])
}
